import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, X, ShieldCheck, CheckCircle2, AlertCircle, Power, PowerOff } from 'lucide-react';
import { apiFetch } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const EMPTY_FORM = { role_name: '', description: '' };

const RoleMaster = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/roles`);
      const json = await res.json();
      setRoles(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      showAlert('error', `Failed to load roles: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoles(); }, []);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditing(item);
    setFormData({ role_name: item.role_name || '', description: item.description || '' });
    setModalOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete role "${name}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/roles/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.status === 1) {
        showAlert('success', 'Role deleted successfully');
        setRoles(prev => prev.filter(r => r.id !== id));
      } else {
        showAlert('error', json.msg || 'Failed to delete role');
      }
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const handleToggleActive = async (item) => {
    const next = item.is_active ? 0 : 1;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/roles/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: next }),
      });
      const json = await res.json();
      if (json.status === 1) {
        setRoles(prev => prev.map(r => r.id === item.id ? { ...r, is_active: next } : r));
        showAlert('success', `Role ${next ? 'activated' : 'deactivated'}`);
      } else {
        showAlert('error', json.msg || 'Failed to update');
      }
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.role_name.trim()) {
      showAlert('error', 'Role name is required');
      return;
    }
    setActionLoading(true);
    try {
      const url = editing ? `${API_BASE_URL}/admin/roles/${editing.id}` : `${API_BASE_URL}/admin/roles`;
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(formData) });
      const json = await res.json();
      if (json.status === 1) {
        showAlert('success', editing ? 'Role updated successfully' : 'Role added successfully');
        setModalOpen(false);
        fetchRoles();
      } else {
        showAlert('error', json.msg || 'Failed to save role');
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = roles.filter(r =>
    (r.role_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColors = [
    { bg: '#fdf4ff', text: '#9333ea', border: '#e9d5ff' },
    { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
    { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
    { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
    { bg: '#f0fdfa', text: '#0d9488', border: '#99f6e4' },
  ];

  return (
    <div className="content-section">
      <div className="content-header">
        <div>
          <h1>Role Management</h1>
          <p className="text-xs text-slate-400 font-medium mt-1">Manage admin roles and permissions</p>
        </div>
        <div className="header-actions">
          <div className="search-container">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="create-btn" onClick={handleOpenAdd}>
            <Plus size={18} /> Add Role
          </button>
        </div>
      </div>

      {alert.show && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-semibold flex items-center gap-3 ${alert.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {alert.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{alert.message}</span>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <ShieldCheck size={48} className="mx-auto mb-3 text-slate-300" />
          <h3>No Roles Found</h3>
          <p>Click "Add Role" to create a new role.</p>
        </div>
      ) : (
        <div className="cards-grid">
          {filtered.map((item, idx) => {
            const color = roleColors[idx % roleColors.length];
            const isActive = item.is_active === 1 || item.is_active === true;
            return (
              <div key={item.id} className="admin-mgmt-card group">
                <div className="admin-mgmt-card-header">
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl border shadow-sm"
                      style={{ background: color.bg, color: color.text, borderColor: color.border }}>
                      <ShieldCheck size={22} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-800 leading-snug">{item.role_name}</h3>
                      <span className="text-[11px] text-slate-400 font-medium">ID: #{item.id}</span>
                    </div>
                  </div>
                  <span className={`status-pill ${isActive ? 'approved' : 'pending'}`}>
                    <span className="dot" />
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="admin-mgmt-card-body">
                  {item.description && (
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.description}</p>
                  )}
                </div>

                <div className="admin-mgmt-card-footer flex justify-between items-center pt-3 border-t border-slate-100">
                  <button
                    onClick={() => handleToggleActive(item)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isActive ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                  >
                    {isActive ? <PowerOff size={13} /> : <Power size={13} />}
                    {isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleOpenEdit(item)} className="p-2 rounded-lg text-slate-500 hover:text-teal-600 hover:bg-teal-50 transition-all" title="Edit Role">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(item.id, item.role_name)} className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete Role">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative">
            <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X size={18} />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">{editing ? 'Edit Role' : 'Add New Role'}</h3>
                <p className="text-xs text-slate-500">Define a portal admin role</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.role_name}
                  onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                  placeholder="e.g. Admin, SuperAdmin, Manager"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/10 font-medium"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Description <span className="text-xs text-slate-400 normal-case font-medium">(Optional)</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this role's permissions..."
                  rows={3}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/10 resize-none"
                />
              </div>
              <div className="pt-2 flex justify-end gap-2.5">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold">Cancel</button>
                <button type="submit" disabled={actionLoading} className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold shadow-md shadow-teal-600/20 disabled:opacity-50">
                  {actionLoading ? 'Saving...' : editing ? 'Update Role' : 'Add Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleMaster;
